import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import {
	useSaveReferralFingerprintMutation,
	useGetReferralCodeByFingerprintMutation,
	useGetReferralLinkMutation,
	useCleanupReferralFingerprintMutation,
} from '@/store/api';
import {
	collectFingerprint,
	extractReferralCodeFromUrl,
	getFromStorage,
	getPublicIpAddress,
	redirectToLobbyOrRoot,
	removeFromStorage,
	saveToStorage,
} from '@/utils';
import { useIsRegisteredUser } from '@/hooks';

interface ReferralContextValue {
	referralCode: string | null;
	isLoading: boolean;
	getReferralCode: () => Promise<string | null>;
}

const ReferralContext = createContext<ReferralContextValue | null>(null);

export function ReferralProvider({ children }: { children: ReactNode }) {
	const [referralCode, setReferralCode] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [hasInitialized, setHasInitialized] = useState(false);

	const [saveFingerprint] = useSaveReferralFingerprintMutation();
	const [lookupByFingerprint] = useGetReferralCodeByFingerprintMutation();
	const [getReferralLink] = useGetReferralLinkMutation();
	const [cleanupFingerprint] = useCleanupReferralFingerprintMutation();

	const isRegisteredUser = useIsRegisteredUser();

	const getFingerprintContext = async () => {
		const fingerprint = collectFingerprint();
		const ipAddress = await getPublicIpAddress();

		return { fingerprint, ipAddress };
	};

	useEffect(() => {
		if (hasInitialized) return;

		const initializeReferral = async () => {
			const codeFromUrl = extractReferralCodeFromUrl();
			if (!codeFromUrl) {
				setHasInitialized(true);
				return;
			}

			const { fingerprint, ipAddress } = await getFingerprintContext();

			const data = await getReferralLink({
				referralCode: codeFromUrl,
			}).unwrap();

			if (isRegisteredUser) {
				await cleanupFingerprint({
					referralCode: codeFromUrl,
					fingerprint,
					ipAddress,
				}).unwrap();

				removeFromStorage();
				redirectToLobbyOrRoot(data);

				setHasInitialized(true);
				return;
			}

			setReferralCode(codeFromUrl);
			saveToStorage(codeFromUrl);

			await saveFingerprint({
				referralCode: codeFromUrl,
				fingerprint,
				ipAddress,
			}).unwrap();

			redirectToLobbyOrRoot(data);

			setHasInitialized(true);
		};

		initializeReferral();
	}, [hasInitialized, saveFingerprint, getReferralLink, cleanupFingerprint, isRegisteredUser]);

	const getReferralCode = useCallback(async (): Promise<string | null> => {
		const codeFromUrl = extractReferralCodeFromUrl();
		if (codeFromUrl) return codeFromUrl;

		if (referralCode) return referralCode;

		const storedCode = getFromStorage();
		if (storedCode) {
			setReferralCode(storedCode);
			return storedCode;
		}

		setIsLoading(true);

		try {
			const { fingerprint, ipAddress } = await getFingerprintContext();

			const result = await lookupByFingerprint({
				fingerprint,
				ipAddress,
			}).unwrap();

			if (result.referralCode) {
				setReferralCode(result.referralCode);
				saveToStorage(result.referralCode);
				return result.referralCode;
			}
		} catch (error) {
			console.error('Failed to lookup referral code by fingerprint:', error);
		} finally {
			setIsLoading(false);
		}

		return null;
	}, [referralCode, lookupByFingerprint]);

	return (
		<ReferralContext.Provider
			value={{
				referralCode,
				isLoading,
				getReferralCode,
			}}
		>
			{children}
		</ReferralContext.Provider>
	);
}

export function useReferral(): ReferralContextValue {
	const ctx = useContext(ReferralContext);
	if (!ctx) {
		throw new Error('useReferral must be used within a ReferralProvider');
	}
	return ctx;
}
